import { Box, type Theme } from "@mui/material";
import type { DateTime } from "luxon";
import { Fragment, memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CoverageCellDisplayMode } from "../../../components/UIToolkit/Coverage/CoverageDeltaCell";
import { CoverageSectionHeader } from "../../../components/UIToolkit/Coverage/CoverageSectionHeader";
import { type CoverageGridContextValue, CoverageGridProvider, type GridCellKey, serializeGridCell } from "./grid/CoverageGridContext";
import { CoverageRowDays } from "./grid/CoverageRowDays";
import { CoverageRowIdentity } from "./grid/CoverageRowIdentity";
import { GridDayHeaderRow } from "./grid/GridDayHeaderRow";
import { GridScrollContainer } from "./grid/GridScrollContainer";
import { GridWeekHeaderRow } from "./grid/GridWeekHeaderRow";
import { StickyLeadingCell } from "./grid/StickyLeadingCell";
import { useCellPopover } from "./grid/useCellPopover";
import { useLinkedHover } from "./grid/useLinkedHover";
import { useToggleSet } from "./grid/useToggleSet";
import { weekHoverTintRules } from "./grid/weekHoverTint";
import { gridColumns, leadingOffsets, ROW_H, totalTracks } from "./gridGeometry";
import { CoverageLocationCreate } from "./LocationConfigEditor/CoverageLocationCreate";
import { CoverageRecurringShiftEditor, type RecurringShiftEditorRequest } from "./RecurringShiftEditor/CoverageRecurringShiftEditor";
import { type CoverageSection, computeSectionRollup, type CoverageRow as RowType } from "./types";

/**
 * Optional location-editing wiring. When supplied (by the live mount), clicking
 * a row's Location cell opens the location-config editor. Omitted in pure mock /
 * prototype renders so the grid still works without an editable backing config.
 */
export type CoverageEditWiring = {
	weekStartDay: number;
	uniqueSections: string[];
	onCoverageRefresh: () => Promise<unknown>;
	/** Hide the edit affordance entirely (disable_edit_master_schedule). */
	editBlocked: boolean;
	editBlockedTooltip: string;
};

// Shared lattice (see gridGeometry.ts) — the standard three leading columns.
const { shift: SHIFT_LEFT, role: ROLE_LEFT } = leadingOffsets("standard");

type Props = {
	sections: CoverageSection[];
	weeks: number;
	/** First day of week 1 (cycle anchor) — drives day/week header dates. */
	cycleStart: DateTime;
	/** How each Summary cell reads its number (signed delta vs `have/need` ratio). */
	displayMode?: CoverageCellDisplayMode;
	/** When provided, the Location cell opens the location-config editor. */
	editWiring?: CoverageEditWiring;
	/** Job-managed dept: the identity column reads each row's `jobName` (backend-supplied) and its
	    header label switches to "Job". Non-job depts (false) keep the plain Role column. */
	isJobDept?: boolean;
};

/** A thin, memoized row: the identity block + the day-cell loop, both memoized in turn. */
const CoverageRow = memo(
	({
		row,
		isExpanded,
		isEditorOpen,
		onToggleExpanded,
		onEditorOpenChange,
	}: {
		row: RowType;
		isExpanded: boolean;
		isEditorOpen: boolean;
		onToggleExpanded: (rowId: string) => void;
		onEditorOpenChange: (rowId: string, open: boolean) => void;
	}) => (
		// `display: contents` carries the ARIA row without adding a box to the CSS grid.
		<Box role="row" sx={{ display: "contents" }}>
			<CoverageRowIdentity
				row={row}
				isExpanded={isExpanded}
				isEditorOpen={isEditorOpen}
				onToggleExpanded={onToggleExpanded}
				onEditorOpenChange={onEditorOpenChange}
			/>
			<CoverageRowDays row={row} isExpanded={isExpanded} />
		</Box>
	),
);

export const CoverageGrid = ({ sections, weeks, cycleStart, displayMode = "delta", editWiring, isJobDept }: Props) => {
	const { t } = useTranslation();
	const [collapsed, toggle] = useToggleSet<string>();
	// Rows expanded in-place to show their assigned names (read-only), per-row detail.
	const [expandedRows, toggleExpanded] = useToggleSet<string>();
	// Linked hover across expanded rows is CSS-driven (no React state / no grid re-render).
	const { gridRef, setHovered } = useLinkedHover();
	// Single-open cell popover: owns the open cell, the open-deferral hack, and the
	// closing cell kept mounted through its exit animation (Phase B).
	const popover = useCellPopover<GridCellKey>(serializeGridCell);
	const [openEditorRowId, setOpenEditorRowId] = useState<string | null>(null);
	// Section-header "+" create: the clicked section's name (null = closed). Mounted
	// only while open so each open starts fresh (pre-seeded with this section).
	const [createSection, setCreateSection] = useState<string | null>(null);

	// One recurring-shift editor request at a time (the modal is fixed-position
	// so it must live at grid level, like the location editor).
	const [recurringRequest, setRecurringRequest] = useState<{ request: RecurringShiftEditorRequest; clickedDate: DateTime } | null>(null);

	const onOpenRecurring = useCallback(
		(request: RecurringShiftEditorRequest, dayIdx: number) => {
			popover.close();
			setRecurringRequest({ request, clickedDate: cycleStart.plus({ days: dayIdx }) });
		},
		[popover.close, cycleStart],
	);

	// Stable per-row handlers (id-parameterized) so memoized rows keep referential equality.
	const onEditorOpenChange = useCallback((rowId: string, open: boolean) => setOpenEditorRowId(open ? rowId : null), []);

	const tracks = totalTracks(weeks);
	// Precompute the cycle's per-day dates once (cells read `dayDates[idx]`).
	const dayDates = useMemo(() => Array.from({ length: weeks * 7 }, (_, i) => cycleStart.plus({ days: i })), [weeks, cycleStart]);

	const gridValue = useMemo<CoverageGridContextValue>(
		() => ({ weeks, cycleStart, dayDates, displayMode, editWiring, popover, onOpenRecurring, setHoveredNurse: setHovered }),
		[weeks, cycleStart, dayDates, displayMode, editWiring, popover, onOpenRecurring, setHovered],
	);

	return (
		<CoverageGridProvider value={gridValue}>
			<GridScrollContainer
				ariaLabel={t("schedule.template.v3.grid.coverageAria")}
				dataTestId="ms3-coverage-grid"
				gridTemplateColumns={gridColumns(weeks)}
				gridRef={gridRef}
				containerSx={[
					// Hover a cell → tint that week header (mirrors today's affordance). One rule per week.
					{ "& .ms3-week-header": { transition: "background-color 150ms" } },
					(theme: Theme) => weekHoverTintRules(theme, weeks),
				]}
			>
				{/* ── Row 1: week headers ── */}
				{/* The Coverage grid is always anchored on today's week, so column 0 is the active one. */}
				<GridWeekHeaderRow weeks={weeks} cycleStart={cycleStart} activeWeekIndex={0} variant="coverage" testIdPrefix="ms3" />

				{/* ── Row 2: leading labels + day-of-week headers ── */}
				<Box role="row" sx={{ display: "contents" }}>
					<StickyLeadingCell left={0} variant="header" role="columnheader" emphasis="muted" sx={{ top: ROW_H }}>
						{t("schedule.template.v3.col.location")}
					</StickyLeadingCell>
					<StickyLeadingCell left={SHIFT_LEFT} variant="header" role="columnheader" emphasis="muted" sx={{ top: ROW_H }}>
						{t("schedule.template.v3.col.shift")}
					</StickyLeadingCell>
					<StickyLeadingCell left={ROLE_LEFT} variant="header" role="columnheader" emphasis="muted" showEdgeShadow sx={{ top: ROW_H }}>
						{t(isJobDept ? "schedule.template.v3.col.job" : "schedule.template.v3.col.role")}
					</StickyLeadingCell>
					<GridDayHeaderRow weeks={weeks} cycleStart={cycleStart} variant="standard" />
				</Box>

				{/* ── Sections ── */}
				{sections.map((section) => {
					const { fillPct, gapCount } = computeSectionRollup(section);
					const isCollapsed = collapsed.has(section.id);
					const canCreate = !!editWiring && !editWiring.editBlocked && !!section.name;
					const sectionHeader = (
						<CoverageSectionHeader
							name={section.name}
							fillPct={fillPct}
							gapCount={gapCount}
							isCollapsed={isCollapsed}
							onToggle={() => toggle(section.id)}
							gridColumn={`1 / span ${tracks}`}
							noTargetsLabel={t("schedule.template.v3.section.noTargets")}
							fillLabel={fillPct === null ? "" : t("schedule.template.v3.section.fill", { pct: fillPct })}
							gapsLabel={t("schedule.template.v3.section.gaps", { count: gapCount })}
							toggleAriaLabel={t("schedule.template.v3.section.toggleAria", { name: section.name })}
							onAdd={canCreate ? () => setCreateSection(section.name) : undefined}
							addLabel={t("schedule.template.v3.section.addLocation", { section: section.name })}
							addDataTestId={`ms3-section-add-${section.id}`}
							dataTestId={`ms3-section-${section.id}`}
						/>
					);
					return (
						<Fragment key={section.id}>
							{/* Full-width section band = one ARIA row whose single cell is a rowheader. */}
							<Box role="row" sx={{ display: "contents" }}>
								<Box role="rowheader" sx={{ display: "contents" }}>
									{editWiring && canCreate ? (
										<CoverageLocationCreate
											open={createSection === section.name}
											onClose={() => setCreateSection(null)}
											weekStartDay={editWiring.weekStartDay}
											uniqueSections={editWiring.uniqueSections}
											initialSection={section.name}
											onCoverageRefresh={editWiring.onCoverageRefresh}
										>
											{sectionHeader}
										</CoverageLocationCreate>
									) : (
										sectionHeader
									)}
								</Box>
							</Box>

							{!isCollapsed &&
								section.rows.map((row) => (
									<CoverageRow
										key={row.id}
										row={row}
										isExpanded={expandedRows.has(row.id)}
										isEditorOpen={openEditorRowId === row.id}
										onToggleExpanded={toggleExpanded}
										onEditorOpenChange={onEditorOpenChange}
									/>
								))}
						</Fragment>
					);
				})}
			</GridScrollContainer>
			{editWiring && recurringRequest && (
				<CoverageRecurringShiftEditor
					request={recurringRequest.request}
					clickedDate={recurringRequest.clickedDate}
					onCoverageRefresh={editWiring.onCoverageRefresh}
					onClose={() => setRecurringRequest(null)}
				/>
			)}
		</CoverageGridProvider>
	);
};
